/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./VIPRData',
	'./data/VIPRData',
	'./VIPRSlotMapping',
	'./VIPRConfig',
	'com/ibm/vipr/VIPR',
	'../interactions/BinningActionsUtils',
	'./properties/PropertiesHelper'
], function(_, Class, VIPRData_Deprecated, VIPRData, VIPRSlotMapping, VIPRConfig, VIPR, BinningActionsUtils, PropertiesHelper) {
	'use strict';

	var VIPRUtils = Class.extend({

		createDataSlots: function(viprDefinition) {
			var vizDef = viprDefinition.vizDef;
			var config = this._getConfiguration(viprDefinition.id);
			var result = this._addToSlots(vizDef.slots, vizDef.dataSets, config);
			return result;
		},

		getPropertyPrefixes: function(viprDefinition) {
			var config = this._getConfiguration(viprDefinition.id);
			return config ? config.propertyPrefixes : undefined;
		},

		getListenForPropChanges: function(viprDefinition) {
			var config = this._getConfiguration(viprDefinition.id);
			return config ? config.listenForPropChanges : undefined;
		},

		getGroupedProperty: function(viprDefinition) {
			var config = this._getConfiguration(viprDefinition.id);
			return config ? config.groupedProperty : undefined;
		},

		includeAllProperties: function(viprDefinition) {
			var config = this._getConfiguration(viprDefinition.id);
			return config && config.includeAllProperties ? config.includeAllProperties : false;
		},

		_addMultiMeasureAndTagsToSlots(continuousSlots, slotCanAcceptMeasureGroupCategorical) {
			continuousSlots[0].multiMeasure = true;
			//to add tags to the slot, if we have a slot that name contains color , we add to that slot, otherwise to first non continues slot.
			const index = slotCanAcceptMeasureGroupCategorical.findIndex(slot => {
				return slot.name.toLowerCase().indexOf('color') !== -1;
			});
			if (index !== -1) {
				slotCanAcceptMeasureGroupCategorical[index].tags = [
					'ca.dashboard.repeatHere=true'
				];
			} else {
				slotCanAcceptMeasureGroupCategorical[0].tags = [
					'ca.dashboard.repeatHere=true'
				];
			}
		},

		// custom vis will support multi measure, based on following rules:
		// if each dataset has only one continues slot and at least one slot that is not continues(cat, any).
		_addMultiMeasureAndTagsToCustomVisDataset(dataset, config) {

			if (config && config.isCustomVis) {
				const continuousSlots = _.filter(dataset.slots, slot => {
					if (slot.segments) {
						return _.filter(slot.segments, segments => {
							return segments.type === 'cont';
						});
					}
					return slot.type === 'cont';
				});
				const slotCanAcceptMeasureGroupCategorical = _.filter(dataset.slots, slot => {
					if (slot.segments) {
						return _.filter(slot.segments, segments => {
							return segments.type !== 'cont' && segments.subType !== 'geospatial';
						});
					}
					return slot.type !== 'cont' && slot.subType !== 'geospatial';
				});
				if (continuousSlots.length == 1 && slotCanAcceptMeasureGroupCategorical.length > 0) {
					this._addMultiMeasureAndTagsToSlots(continuousSlots, slotCanAcceptMeasureGroupCategorical);
				}
			}
		},


		_addToSlots: function(slots = [], datasets, config) {
			const aSlots = {};
			if (!_.isEmpty(datasets)) {
				var datasetName;
				_.each(datasets, function(dataset) {
					datasetName = dataset.name;
					this._addMultiMeasureAndTagsToCustomVisDataset(dataset, config);
					_.each(dataset.slots, function(slot) {
						if (!_.isEmpty(slot.segments)) {
							var oSlot;
							_.each(slot.segments, function(segment) {
								//VIPR confirms segment is sub class of VIPR slot
								oSlot = this._buildSlot(segment, config, datasetName);
								oSlot.group = slot.name;
								if(aSlots[segment.name]) {
									aSlots[segment.name].datasetIdList.push(datasetName);
								} else {
									aSlots[segment.name] = oSlot;
								}
							}.bind(this));
						} else {
							if(aSlots[slot.name]) {
								aSlots[slot.name].datasetIdList.push(datasetName);
							} else {
								aSlots[slot.name] = this._buildSlot(slot, config, datasetName);
							}
						}
					}.bind(this));
				}.bind(this));
			}

			// Keep slots in the vida order, important for shared slots like in compositev2
			const slotOrder = [];
			slots.forEach(slot => {
				if(!_.isEmpty(slot.segments)) {
					slot.segments.forEach(segment => {
						slotOrder.push(segment.name);
					});
				} else {
					slotOrder.push(slot.name);
				}
			});
			return slotOrder.map((name) => {
				return aSlots[name];
			});
		},

		_buildSlot: function(slot, config, datasetName) {
			var oCurSlot = this._getSlotObject(slot, config, datasetName);
			return (config && config.slots) ? _.extend(oCurSlot, config.slots[oCurSlot.id]) : oCurSlot;
		},

		_getSlotObject: function(vizDefSlot, config, datasetName) {
			var slotType = this._getSlotType(vizDefSlot);
			var slot = {
				id: vizDefSlot.name,
				caption: vizDefSlot.caption,
				type: slotType,
				role: vizDefSlot.role,
				maxStackItems: vizDefSlot.maxLevels,
				optional: vizDefSlot.optional,
				multiplier: this._isMultiplier(vizDefSlot),
				stackItems: slotType !== 'ordinal',
				multiItems: vizDefSlot.multiDataItem,
				coachMark: vizDefSlot.coachMark
			};
			if (vizDefSlot.useCategoryAsValue) {
				slot.useCategoryAsValue = vizDefSlot.useCategoryAsValue;
			}
			if (datasetName) {
				slot.datasetIdList = [datasetName];
			}
			if (vizDefSlot.multiMeasure) {
				slot.multiMeasure = vizDefSlot.multiMeasure;
			}
			if (vizDefSlot.tags) {
				slot.tags = vizDefSlot.tags;
			}
			return (config && config.slots && config.slots[slot.id]) ? _.extend(slot, config.slots[slot.id]) : slot;
		},

		_getSlotType: function(slot) {
			if (slot.type === 'cat') {
				return 'category';
			} else if (slot.type === 'cont') {
				return 'ordinal';
			}
			return slot.type;
		},

		_isMultiplier: function(slot) {
			var multipliers = [
				'repeatHorizontal',
				'repeatVertical'
			];
			return multipliers.indexOf(slot.name) >= 0;
		},

		/**
		* @param {String} visId - id of the specic vis of interest
		* @returns {Object} single palette prop descriptions
		*'singlePaletteProps': {
		*	column_color: {
		*		id: 'defaultPaletteIndex',
		*		labelId: 'propColumnColor',
		*		defaultColorIndex: 0
		*	},
		*	line_color: {
		*		id: 'lineColor',
		*		labelId: 'propLineAndSymbolColor',
		*		defaultColorIndex: 1
		*	}
		*}
		*/
		getSinglePaletteProperties: function(visId) {
			const config = this._getConfiguration(visId);
			return config && config.colorPalettes && config.colorPalettes.single;
		},

		getViprWidgetPropertyInstances(viprProperties, propertyIds) {
			const propertyInstances = [];
			propertyIds.forEach(propertyId => {
				const propertyInstance = viprProperties.get(propertyId);
				if (propertyInstance){
					propertyInstances.push(propertyInstance);
				}
			});
			return propertyInstances;
		},

		/**
		* Get single palettes by checking the configuration
		* Preferable to check the config for single palette props, because it has properties like defaultColorIndex that arent included in the vizBundle
		* If no single palette config is available, check the viprProperties
		* @returns {Object[]} Single palette property array
		*/
		getSinglePalettes(viprProperties, visId){
			const config = this._getConfiguration(visId);
			const paletteIds = (config && config.colorPalettes && config.colorPalettes.single && Object.keys(config.colorPalettes.single)) || [];
			if (paletteIds.length === 0){
				return this.getSinglePalettesFromWidget(viprProperties);
			}
			return this.getViprWidgetPropertyInstances(viprProperties, paletteIds);
		},

		/**
		* Get single palettes by checking the viprProperties itself
		* @returns {Object[]} Single palette property array
		*/
		getSinglePalettesFromWidget(viprProperties) {
			const paletteIds = [];
			viprProperties.forEach(prop => {
				if (prop.type === 'color' && prop.tags && prop.tags.indexOf( 'palette' ) !== -1) {
					paletteIds.push(prop.name);
				}
			});
			return this.getViprWidgetPropertyInstances(viprProperties, paletteIds);
		},

		/**
		* Get all color palettes
		* @returns {Object[]} Palette property array
		*/
		getPalettes(viprProperties, visId) {
			const paletteIds = [];
			viprProperties.forEach(prop => {
				if ( prop.type === 'palette' ) {
					paletteIds.push(prop.name);
				}
			});
			return this.getViprWidgetPropertyInstances(viprProperties, paletteIds).concat(this.getSinglePalettes(viprProperties, visId));
		},

		/**
		* @returns the default value from the bundle config if defined, undefined otherwise.
		*/
		getDefaultValueForProperty(bundleId, propertyName) {
			let defaultValue;
			const config = this._getConfiguration(bundleId);
			// If the property is defined in the config file get it.
			if (config && config.properties && config.properties[propertyName]) {
				defaultValue = config.properties[propertyName].defaultValue;
			}
			return defaultValue;
		},

		/**
		* Get the overriden property description from the configuration file if there is any.
		* @param {String} bundleId - id of the vis bundle
		* @param {String} propertyName - name of the property of interest
		*/
		getOverridenDefaultForProperty: function(bundleId, propertyName) {
			let result = {
				defaultValue: null,
				isActive: false
			};
			const config = this._getConfiguration(bundleId);
			// If the property is defined in the config file get it.
			if (config && config.properties && config.properties[propertyName]) {
				result.defaultValue = config.properties[propertyName].defaultValue;
				result.isActive = config.properties[propertyName].active;
				result.isReadOnly = config.properties[propertyName].isReadOnly;
				result.checkForValidValue = config.properties[propertyName].checkForValidValue;
			}
			return result;
		},

		/**
		* @param {String} bundleId - Vis bundle id
		* @param {Object} property - Description of the property of interest
		* @param {String} propertyName - name of the property of interest. Note, we
		* can not just use the name in the property param as we may be using a deprecated
		* property which means the property would have the new prop name.
		*/
		overrideProperty: function(bundleId, property, propertyName) {
			var config = this._getConfiguration(bundleId);
			if (config && config.properties && config.properties[propertyName]) {
				// override  the property
				// Need to clone the config prop or it will change the prop in the model.
				let clonedProp = _.clone(config.properties[propertyName]);
				property = _.defaults(clonedProp, property);
				property.overridden = true;
			}
			return property;
		},

		isRenderWithoutCompletingMapping: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return !!(config && config.renderWithoutCompletingMapping === true); //Default: false
		},

		isRecommendable: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return !(config && config.isRecommendable === false); //Default: true
		},

		/**
		 * Determine whether the property name is part of the inclusion list
		 * @param {string} bundleId visualization id
		 * @param {string} propertyName name of the property
		 */
		isPropertyIncluded: function(bundleId, propertyName) {
			var configuration = this._getConfiguration(bundleId);
			return configuration && _.contains(configuration.config.include, propertyName);
		},

		/**
		 * Determine whether the property name is part of the exclusion list
		 * @param {string} bundleId visualization id
		 * @param {string} propertyName name of the property
		 */
		isPropertyExcluded: function(bundleId, propertyName) {
			var configuration = this._getConfiguration(bundleId);
			return _.contains(configuration.config.exclude, propertyName);
		},

		noDataQuery: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return !!(config && config.noDataQuery === true); //Default: false
		},

		/**
		* @return true if the charts are configrured to use FPD response as Chart-Insights API input
		*/
		useFPDAsInsightsInput: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return !!(config && config.useFPDAsInsightsInput === true); //Default: false
		},

		isDisablePan: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return !!(config && config.disablePan === true); //Default: false
		},

		aggregateAndSortOnClient: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return !!(config && config.aggregateAndSortOnClient === true); //Default: false
		},

		/*Discussed with VIPR, currently we still need Data Row Limit guard*/
		getDataRowLimit: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return config && config.dataRowLimit;
		},

		/*Data Row Limit guard for IE*/
		getDataRowLimitIE: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return config && config.dataRowLimitIE;
		},

		getVisualizationTabs: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return config && config.tabs;
		},

		getQueryHints: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return config ? config.queryHints : null;
		},

		doesConfigPropertyMatchExpected: function(bundleId, configObjectProperty, expectedValue) {
			if (bundleId) {
				var config = this._getConfiguration(bundleId);
				if (config) {
					return config[configObjectProperty] ? config[configObjectProperty] === expectedValue : false;
				}
			}
			return false;
		},

		getBinning: function(bundleId) {
			var config = this._getConfiguration(bundleId);
			return config && config.binning ? config.binning : {};
		},

		canApplyAutoBin: function(bundleId) {
			const config = this.getBinning(bundleId);
			const doesConfigSupportAutobinning = this.doesWidgetConfigSupportAutobinning(config);
			return doesConfigSupportAutobinning;
		},

		doesWidgetConfigSupportAutobinning: (config) => BinningActionsUtils.doesWidgetConfigSupportAutobinning(config),

		_getConfiguration: function(id) {
			return VIPRConfig.getConfig(id);
		},

		_getConfigVal: function(id, key) {
			if (this._getConfiguration(id) && this._getConfiguration(id)[key]) {
				return this._getConfiguration(id)[key];
			}
			return false;
		},

		applyRequiredConfiguration: function(viprDefinitionId, requiredConfig, propertyValues){
			// Merge the overrides into the existing configuration
			var lib = this._getConfiguration(viprDefinitionId);
			var override = lib.config.override;
			if ( !override ) {
				// If there are no overrides yet, create a new object
				override = {};
				lib.config.override = override;
			}
			_.each(requiredConfig, function (property) {
				var value = propertyValues[property];
				// See if there already is an override definition for this property. Otherwise, create it.
				if ( !override[property] ) {
					override[property] = {};
				}
				override[property].defaultValue = value;
			});
		},

		//TODO: revise for properties support
		_applyPropertyType: function(prop, vizProp) {
			if (vizProp.type === 'color') {
				prop.type = 'ColorPicker';
				prop.paletteType = 'ColorPalette';
			}			else if (vizProp.type === 'boolean') {
				prop.type = 'CheckBox';
				prop.defaultValue = vizProp.defaultValue;
			}			else if (vizProp.type === 'number') {
				// TODO
				prop.type = 'CheckBox';
			}			else if (vizProp.type === 'enum') {
				prop.type = 'DropDown';
				prop.options = _.map(vizProp.possibleValues, function(option) {
					return {
						label: option.caption,
						value: option.name
					};
				});
			}
			return prop;
		},

		/**
		 * @deprecated
		 * @param  {Array of JSONObject} inputPayload
		 * @return {VIPRData}  A Collection of VIPRDataSet
		**/
		createData: function(queryResults, visualization, content) {
			const hideAggTypeInAxisLabels = this.doesConfigPropertyMatchExpected(visualization.getDefinition().getId(), 'hideAggTypeInAxisLabels', true);
			return new VIPRData_Deprecated(queryResults, visualization, hideAggTypeInAxisLabels, content);
		},

		createQueryResultData: function(queryResults, visualization, content) {
			const hideAggTypeInAxisLabels = this.doesConfigPropertyMatchExpected(visualization.getDefinition().getId(), 'hideAggTypeInAxisLabels', true);
			return new VIPRData(queryResults, visualization, hideAggTypeInAxisLabels, content);
		},

		createSlotMapping: function(visualization) {
			return new VIPRSlotMapping(visualization);
		},

		createMetaDataEntries: function(vizDefinition) {
			return vizDefinition.metaDataEntries.slice();
		},

		isRequiredConfig: function(id, prop){
			return this.getRequiredConfig(id) && this.getRequiredConfig(id).indexOf(prop.name)>-1;
		},

		getRequiredConfig: function(id){
			return this._getConfigVal(id, 'requiredConfig');
		},

		getCursor: function(id){
			return this._getConfigVal(id, 'cursor');
		},

		isGeospatial: function(id) {
			return this._getConfigVal(id, 'geo');
		},

		supportsSortAction: function(id) {
			return !this._getConfigVal(id, 'doesNotSupportSortAction');
		},

		supportsAnnotations: function(id) {
			return !this._getConfigVal(id, 'doesNotSupportAnnotations');
		},

		supportsContextualTopBottomAction: function(id) {
			return !this._getConfigVal(id, 'doesNotSupportTopBottomAction');
		},

		supportsNonContextualTopBottomAction: function (id) {
			return !this._getConfigVal(id, 'doesNotSupportTopBottomAction');
		},

		supportsFormatAction: function(id) {
			return !this._getConfigVal(id, 'doesNotSupportFormatAction');
		},

		supportsCustomGroupAction: function(id) {
			return !this._getConfigVal(id, 'doesNotSupportCustomGroupAction');
		},

		supportsMaintainAxisScale: function(id) {
			return this._getConfigVal(id, 'supportsMaintainAxisScale');
		},

		supportsCustomDataSelection: function(id) {
			return this._getConfigVal(id, 'enableCustomDataSelection');
		},

		/**
		 * create a vida font object
		 * @param vidaFontValues - an array of font props values [family,size,style,weight], e.g.: ['Anton', '12px', 'italic', 'weight']
		 * @returns {Object} return a vida font object, VIPR parse method would return null if the values don't parse
		 * {
		 *   'family': [],
		 *   'size': value,
		 *   'style': value,
		 *   'weight': value
		 * }
		 */
		createVidaFontProperty(vidaFontValues) {
			if (vidaFontValues){
				const family = VIPR.property.Font.parseFamily(vidaFontValues[0]);
				const size = VIPR.property.Length.parse(vidaFontValues[1]);
				const style = VIPR.property.FontStyle.parse(vidaFontValues[2]);
				const weight = VIPR.property.FontWeight.parse(vidaFontValues[3]);
				return VIPR.property.Font.create(family, size, style, weight);
			}
		},

		/**
		 * parse a valid string to a vida font object
		 * @param fontPropValue - a  font-style string, e.g.: 'Anton 12px italic weight' or 'font-family: Condiment-Regular; font-size: 24px;'
		 * @returns {Object} return a vida font object,
		 * {
		 *   'family': [],
		 *   'size': value,
		 *   'style': value,
		 *   'weight': value
		 * }
		 */
		parseVidaFontProperty(fontPropValue) {
			return VIPR.property.Font.parse(fontPropValue);
		},

		getViprProperiesAndGroups(viprDefinition, properties, groups) {
			const viprProperties = viprDefinition.vizDef.properties;
			_.forEach(viprDefinition.vizDef.configuration, config => {
				properties.push(config);
			});
			return this._getViprProperiesAndGroups(viprProperties, properties, groups);
		},

		_getViprProperiesAndGroups(viprProperties, properties, groups) {
			_.forEach(viprProperties, viprProperty => {
				if (viprProperty.type === 'group') {
					let propertiesName = _.pluck(viprProperty.properties, 'name');
					groups[viprProperty.name] = propertiesName;
					properties.push(viprProperty);
					return this._getViprProperiesAndGroups(viprProperty.properties, properties, groups);
				} else {
					return properties.push(viprProperty);
				}
			});
		},

		_getPropertiesByNames(properties, propertiesNames) {
			let result = [];
			_.forEach(propertiesNames, (name) => {
				let property =  _.find(properties, property => {
					return property.name === name;
				});
				if (property) {
					result.push(property);
				}
			});
			return result;
		},

		_getPropertiesByType(properties, type) {
			return _.filter(properties, property => property.type === type);
		},

		buildVidaFontPropertiesFromParts(fontChanges, currentPropertyValue){
			return PropertiesHelper.buildVidaFontPropertiesFromParts(this, fontChanges, currentPropertyValue);
		},

		getVidaFontPropertiesPart(part, fontPropertyValue){
			return PropertiesHelper.getVidaFontPropertiesPart(this, part, fontPropertyValue);
		}
	});

	// create a singleton instance
	return new VIPRUtils();
});
